import { useState, useEffect, useMemo, useRef } from "react";
import { fetchTeamRoster } from "../../../services/team-service";

export const useRoster = ({ teamId, years }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [selectedYear, setSelectedYear] = useState(years?.[0] ?? null);
  const isInitializingRef = useRef(true);
  const skipNextEffectRef = useRef(false);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const controller = new AbortController();

    (async () => {
      setStatus("loading");
      try {
        const res = await fetchTeamRoster(teamId, selectedYear, controller.signal);
        if (controller.signal.aborted) return;

        setData(res);

        if (isInitializingRef.current && !selectedYear && res?.available_years?.length) {
          isInitializingRef.current = false;
          skipNextEffectRef.current = true;
          if (!controller.signal.aborted) setSelectedYear(res.available_years[0]);
        } else {
          isInitializingRef.current = false;
        }

        if (!controller.signal.aborted) setStatus("ready");
      } catch (e) {
        if (e?.name !== "AbortError") {
          setStatus("error");
          isInitializingRef.current = false;
        }
      }
    })();

    return () => controller.abort();
  }, [teamId, selectedYear]);

  const availableYears = useMemo(() => data?.available_years || years || [], [data, years]);
  const roster = useMemo(() => data?.roster || [], [data]);

  return { status, selectedYear, setSelectedYear, availableYears, roster };
};
